import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  BugReport as BugReportIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

interface JobInfo {
  id: string;
  name: string;
  status: 'completed' | 'running' | 'scheduled' | 'failed';
  next_run_time?: string;
  last_run_time?: string;
  created_at: string;
  func: string;
  args: any[];
  kwargs: any;
  trigger: string;
  misfire_grace_time?: number;
  coalesce?: boolean;
  max_instances?: number;
  next_run_time_utc?: string;
  last_run_time_utc?: string;
}

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.auth);
  const [jobs, setJobs] = useState<JobInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Campaign debugging state
  const [campaignDebugOpen, setCampaignDebugOpen] = useState(false);
  const [campaignDebugData, setCampaignDebugData] = useState<any>(null);
  const [campaignDebugLoading, setCampaignDebugLoading] = useState(false);
  const [allCampaignsDebug, setAllCampaignsDebug] = useState<any[]>([]);
  const [showAllCampaignsDebug, setShowAllCampaignsDebug] = useState(false);

  // Check if user is authorized (only narayana)
  useEffect(() => {
    if (user?.username !== 'narayana') {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/v1/admin/scheduler-jobs', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setJobs(data.jobs || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  // Campaign debugging functions
  const fetchAllCampaignsDebug = async () => {
    try {
      setCampaignDebugLoading(true);
      const response = await fetch('/api/v1/admin/campaign-debug', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAllCampaignsDebug(data.campaigns || []);
      setShowAllCampaignsDebug(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch campaign debug info');
    } finally {
      setCampaignDebugLoading(false);
    }
  };

  const fetchCampaignDebug = async (campaignId: number) => {
    try {
      setCampaignDebugLoading(true);
      const response = await fetch(`/api/v1/admin/campaign-debug/${campaignId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setCampaignDebugData(data);
      setCampaignDebugOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch campaign debug info');
    } finally {
      setCampaignDebugLoading(false);
    }
  };

  const testCampaignCall = async (campaignId: number) => {
    try {
      const response = await fetch(`/api/v1/admin/campaign-debug/${campaignId}/test-call`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      alert(data.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test campaign call');
    }
  };

  const recreateCampaignJob = async (campaignId: number) => {
    try {
      const response = await fetch(`/api/v1/admin/campaign-debug/${campaignId}/recreate-job`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      alert(data.message);
      fetchJobs(); // Refresh the jobs list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to recreate campaign job');
    }
  };

  useEffect(() => {
    if (user?.username === 'narayana') {
      fetchJobs();
    }
  }, [user]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'running':
        return <PlayIcon color="primary" />;
      case 'scheduled':
        return <ScheduleIcon color="info" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'primary';
      case 'scheduled':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateTime: string | undefined, userTimezone: string = 'US/Eastern') => {
    if (!dateTime) return 'N/A';
    const utcDate = new Date(dateTime);
    
    // Use Intl.DateTimeFormat for proper timezone conversion
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    const parts = formatter.formatToParts(utcDate);
    const formattedDate = parts.map(part => part.value).join('');
    
    // Get timezone abbreviation
    const timeZoneFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      timeZoneName: 'short'
    });
    
    const timeZoneName = timeZoneFormatter.formatToParts(utcDate)
      .find(part => part.type === 'timeZoneName')?.value || '';
    
    return `${formattedDate} ${timeZoneName}`;
  };

  const sortJobs = (jobs: JobInfo[]) => {
    return jobs.sort((a, b) => {
      // First sort by status priority
      const statusOrder = { running: 0, scheduled: 1, completed: 2, failed: 3 };
      const statusA = statusOrder[a.status as keyof typeof statusOrder] ?? 4;
      const statusB = statusOrder[b.status as keyof typeof statusOrder] ?? 4;
      
      if (statusA !== statusB) {
        return statusA - statusB;
      }
      
      // Then sort by date/time (most recent first)
      const dateA = new Date(a.next_run_time || a.last_run_time || a.created_at);
      const dateB = new Date(b.next_run_time || b.last_run_time || b.created_at);
      return dateB.getTime() - dateA.getTime();
    });
  };

  if (user?.username !== 'narayana') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access denied. This page is only available for authorized administrators.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchJobs} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Container>
    );
  }

  const sortedJobs = sortJobs(jobs);
  const jobStats = {
    total: jobs.length,
    running: jobs.filter(j => j.status === 'running').length,
    scheduled: jobs.filter(j => j.status === 'scheduled').length,
    completed: jobs.filter(j => j.status === 'completed').length,
    failed: jobs.filter(j => j.status === 'failed').length,
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          APScheduler Jobs Management
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Jobs
              </Typography>
              <Typography variant="h4">
                {jobStats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Running
              </Typography>
              <Typography variant="h4" color="primary">
                {jobStats.running}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Scheduled
              </Typography>
              <Typography variant="h4" color="info.main">
                {jobStats.scheduled}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" color="success.main">
                {jobStats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Failed
              </Typography>
              <Typography variant="h4" color="error.main">
                {jobStats.failed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Campaign Debugging Section */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Campaign Debugging
          </Typography>
          <Button
            variant="outlined"
            startIcon={<BugReportIcon />}
            onClick={fetchAllCampaignsDebug}
            disabled={campaignDebugLoading}
          >
            {campaignDebugLoading ? 'Loading...' : 'Debug All Campaigns'}
          </Button>
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use this section to debug campaign calling issues. Check scheduler jobs, test calls, and recreate jobs.
          </Typography>
          
          {showAllCampaignsDebug && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Campaign Debug Results</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {allCampaignsDebug && allCampaignsDebug.length > 0 ? allCampaignsDebug.map((campaign) => (
                    <Grid item xs={12} md={6} key={campaign.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">{campaign.name || 'Unnamed Campaign'}</Typography>
                            <Chip 
                              label={campaign.status || 'unknown'} 
                              color={campaign.status === 'active' ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                          
                          <List dense>
                            <ListItem>
                              <ListItemIcon>
                                <ScheduleIcon />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Scheduler Job" 
                                secondary={campaign.scheduler_job?.exists ? '✅ Exists' : '❌ Missing'}
                              />
                            </ListItem>
                            
                            <ListItem>
                              <ListItemIcon>
                                <CheckCircleIcon />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Progress" 
                                secondary={`${campaign.completed_contacts || 0}/${campaign.total_contacts || 0} contacts`}
                              />
                            </ListItem>
                            
                            <ListItem>
                              <ListItemIcon>
                                <WarningIcon />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Next Run" 
                                secondary={campaign.scheduler_job?.next_run_time || 'Not scheduled'}
                              />
                            </ListItem>
                          </List>
                          
                          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => fetchCampaignDebug(campaign.id)}
                              disabled={campaignDebugLoading}
                            >
                              Debug Details
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => testCampaignCall(campaign.id)}
                            >
                              Test Call
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => recreateCampaignJob(campaign.id)}
                            >
                              Recreate Job
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )) : (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        No campaigns found for debugging.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </Paper>

      {/* Jobs Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Scheduler Jobs
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchJobs}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
        <Divider />
        <TableContainer>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Job Name</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Function</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Next Run</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Last Run</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Trigger</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedJobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(job.status)}
                      <Chip
                        label={job.status?.toUpperCase() || 'UNKNOWN'}
                        color={getStatusColor(job.status) as any}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {job.name || 'Unknown Job'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {job.func || 'Unknown Function'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTime(job.next_run_time, user?.timezone)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTime(job.last_run_time, user?.timezone)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {job.trigger || 'Unknown Trigger'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTime(job.created_at, user?.timezone)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {jobs.length === 0 && (
        <Alert severity="info">
          No scheduler jobs found.
        </Alert>
      )}

      {/* Campaign Debug Dialog */}
      <Dialog
        open={campaignDebugOpen}
        onClose={() => setCampaignDebugOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Campaign Debug Details</DialogTitle>
        <DialogContent>
          {campaignDebugData && (
            <Box>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Campaign Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    <ListItem>
                      <ListItemText primary="Name" secondary={campaignDebugData.campaign?.name || 'Unknown'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Status" secondary={campaignDebugData.campaign?.status || 'Unknown'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Interval" secondary={`${campaignDebugData.campaign?.interval_minutes || 0} minutes`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Timezone" secondary={campaignDebugData.campaign?.timezone || 'Unknown'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Calling Hours" secondary={`${campaignDebugData.campaign?.start_hour || 0}:00 - ${campaignDebugData.campaign?.end_hour || 0}:00`} />
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Scheduler Job Status</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        {campaignDebugData.scheduler_job?.exists ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />}
                      </ListItemIcon>
                      <ListItemText 
                        primary="Job Exists" 
                        secondary={campaignDebugData.scheduler_job?.exists ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Next Run" secondary={campaignDebugData.scheduler_job?.next_run_time || 'Not scheduled'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Trigger" secondary={campaignDebugData.scheduler_job?.trigger} />
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Calling Window</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        {campaignDebugData.calling_window?.is_valid ? <CheckCircleIcon color="success" /> : <WarningIcon color="warning" />}
                      </ListItemIcon>
                      <ListItemText 
                        primary="Window Valid" 
                        secondary={campaignDebugData.calling_window?.is_valid ? 'Yes' : 'No'} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Current Time" secondary={campaignDebugData.calling_window?.current_time} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Days of Week" secondary={campaignDebugData.calling_window?.days_of_week?.join(', ')} />
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Contact Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    <ListItem>
                      <ListItemText primary="Total Contacts" secondary={campaignDebugData.contact_stats?.total || 0} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Completed" secondary={campaignDebugData.contact_stats?.completed || 0} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Pending" secondary={campaignDebugData.contact_stats?.pending || 0} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Failed" secondary={campaignDebugData.contact_stats?.failed || 0} />
                    </ListItem>
                    {campaignDebugData.next_contact && (
                      <ListItem>
                        <ListItemText primary="Next Contact" secondary={`${campaignDebugData.next_contact.phone || 'Unknown'} (Attempt ${(campaignDebugData.next_contact.current_attempt || 0) + 1})`} />
                      </ListItem>
                    )}
                  </List>
                </AccordionDetails>
              </Accordion>

              {campaignDebugData.recent_call_logs && campaignDebugData.recent_call_logs.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Recent Call Logs</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {campaignDebugData.recent_call_logs.map((log: any) => (
                        <ListItem key={log.id}>
                          <ListItemText 
                            primary={`${log.phone_number} (Attempt ${log.attempt_number})`}
                            secondary={`${log.status} - ${log.created_at}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDebugOpen(false)}>Close</Button>
          {campaignDebugData && (
            <>
              <Button 
                onClick={() => testCampaignCall(campaignDebugData.campaign.id)}
                variant="outlined"
              >
                Test Call
              </Button>
              <Button 
                onClick={() => recreateCampaignJob(campaignDebugData.campaign.id)}
                variant="contained"
              >
                Recreate Job
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPage;
